import { theme } from 'antd'
import type { ThemeConfig } from 'antd'
import type { FormTheme, PreviewAppearance } from './public-form'

function luminance(hex: string) {
    const rgb = hex
        .slice(1)
        .match(/../g)!
        .map((value) => parseInt(value, 16) / 255)
        .map((value) => (value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4))
    return rgb[0] * 0.2126 + rgb[1] * 0.7152 + rgb[2] * 0.0722
}

export function contrastRatio(a: string, b: string) {
    const x = luminance(a),
        y = luminance(b)
    return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05)
}

function solidTextColor(background: string) {
    return contrastRatio('#000000', background) > contrastRatio('#ffffff', background) ? '#000000' : '#ffffff'
}

function linkColor(primary: string, background: string) {
    const target = solidTextColor(background) === '#000000' ? 0 : 255
    const rgb = primary
        .slice(1)
        .match(/../g)!
        .map((value) => parseInt(value, 16))
    // Keep the brand hue, adjusting it only as far as readable link text requires.
    for (let step = 0; step <= 100; step++) {
        const color =
            '#' +
            rgb
                .map((value) =>
                    Math.round(value + ((target - value) * step) / 100)
                        .toString(16)
                        .padStart(2, '0')
                )
                .join('')
        if (contrastRatio(color, background) >= 4.5) return color
    }
    return solidTextColor(background)
}

export function publicFormTheme(activeTheme: FormTheme, appearance: PreviewAppearance): ThemeConfig {
    const baseAlgorithm = appearance === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm
    const resolved = theme.getDesignToken({ algorithm: baseAlgorithm, token: activeTheme })
    const colorLink = linkColor(activeTheme.colorPrimary, resolved.colorBgContainer)
    // Link colors are seed tokens, so the dark algorithm would otherwise dim them again.
    const algorithm: typeof theme.defaultAlgorithm = (seed) => ({
        ...baseAlgorithm(seed),
        colorLink,
    })
    return {
        inherit: false,
        algorithm,
        components: {
            Form: { itemMarginBottom: 20, verticalLabelPadding: '0 0 8px' },
            Card: { bodyPadding: 24 },
        },
        token: {
            ...activeTheme,
            colorLinkHover: colorLink,
            colorLinkActive: colorLink,
        },
    }
}

export function hasLowFormContrast(activeTheme: FormTheme, appearance: PreviewAppearance) {
    const resolved = theme.getDesignToken(publicFormTheme(activeTheme, appearance))
    // Check body text and links. Primary buttons use Ant Design's standard white text.
    return (
        contrastRatio(resolved.colorText, resolved.colorBgContainer) < 4.5 ||
        contrastRatio(resolved.colorText, resolved.colorBgLayout) < 4.5 ||
        contrastRatio(resolved.colorLink, resolved.colorBgContainer) < 4.5
    )
}
